import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Loader2, Shield, Lock, ArrowLeft } from 'lucide-react';
import { FlutterWaveButton } from 'flutterwave-react-v3';
import api from '../services/api';
import toast from 'react-hot-toast';

const Payment = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [flutterwaveKey, setFlutterwaveKey] = useState('');

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const [reqRes, keyRes] = await Promise.all([
          api.get(`/requests/${requestId}`),
          api.get('/payments/flutterwave-key'),
        ]);
        setRequest(reqRes.data.request);
        if (keyRes.data.publicKey) setFlutterwaveKey(keyRes.data.publicKey);
      } catch (err) {
        toast.error('Request not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchRequest();
  }, [requestId, navigate]);

  useEffect(() => {
    if (request && flutterwaveKey && !paymentData) {
      api.post('/payments/initialize', { requestId })
        .then((res) => {
          const { txRef, amount, email, name } = res.data;
          if (txRef) setPaymentData({ txRef, amount, email, name });
        })
        .catch(() => {});
    }
  }, [request, flutterwaveKey, requestId, paymentData]);

  const onFlutterwaveSuccess = async (response) => {
    if (response.status === 'successful' || response.status === 'completed') {
      try { await api.get(`/payments/verify/${paymentData.txRef}`); } catch (_) {}
      toast.success('Payment successful!');
      setTimeout(() => navigate('/'), 1500);
    } else {
      toast.error('Payment was not successful');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <Loader2 className="animate-spin text-primary-400" size={32} />
      </div>
    );
  }

  if (!request) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-lg mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft size={18} /> Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CreditCard className="text-primary-400" size={32} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Complete Payment</h1>
            <p className="text-gray-400">Secure payment via Flutterwave</p>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex justify-between py-3 border-b border-gray-800">
              <span className="text-gray-400">Name</span>
              <span className="font-medium">{request.fullName}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-gray-800">
              <span className="text-gray-400">Country</span>
              <span className="font-medium">{request.desiredCountry}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-gray-800">
              <span className="text-gray-400">Username</span>
              <span className="font-medium">{request.username || 'N/A'}</span>
            </div>
            <div className="flex justify-between py-3 text-lg font-bold">
              <span>Total</span>
              <span className="gradient-text">₦5,000</span>
            </div>
          </div>

          {paymentData && flutterwaveKey && (
            <FlutterWaveButton
              amount={paymentData.amount}
              currency="NGN"
              tx_ref={paymentData.txRef}
              payment_options="card,banktransfer,ussd"
              customer={{
                email: paymentData.email,
                name: paymentData.name || paymentData.email,
              }}
              customizations={{
                title: 'Advance Advert Tv Digital',
                description: 'Foreign TikTok Account Purchase',
              }}
              public_key={flutterwaveKey}
              callback={onFlutterwaveSuccess}
              onClose={() => {}}
              text={<span className="flex items-center justify-center gap-2"><CreditCard size={20} /> Pay ₦5,000 Now</span>}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white font-bold text-lg shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all"
            />
          )}

          <div className="flex items-center justify-center gap-4 mt-6 text-sm text-gray-400">
            <div className="flex items-center gap-1"><Lock size={14} /> SSL Encrypted</div>
            <div className="flex items-center gap-1"><Shield size={14} /> Secure Payment</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Payment;
